import { formatDate } from '@angular/common';
import { Component, HostListener, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  combineLatest,
  filter,
  finalize,
  first,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap,
  catchError,
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmModalComponent } from 'src/app/core/components/confirm-modal/confirm-modal.component';
import { Constants } from 'src/app/core/constants';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { AttachmentWithDetails, Attachment } from 'src/app/core/models/project/attachment/attachment.interface';
import { ContactActivity } from 'src/app/core/models/contact/contact-activity.interface';
import { ContactDeliveryAddress } from 'src/app/core/models/contact/contact-delivery-address.interface';
import { DetailedContact } from 'src/app/core/models/contact/detailed-contact.interface';
import { DetailedContactFormModel } from 'src/app/core/models/contact/detailed-contact-form.model';
import { ContactNote } from 'src/app/core/models/contact/contact-note.interface';
import { CustomFile } from 'src/app/core/models/customFile.interface';
import { MinimalOrganization } from 'src/app/core/models/organization/minimal-organization.interface';
import { TabItem } from 'src/app/core/models/tabs/tab-item.interface';
import { MinimalProfile } from 'src/app/core/models/user/profile';
import { ActionMenuItem } from 'src/app/core/components/action-menu/action-menu-item.interface';
import { ContactService } from 'src/app/core/services/contact.service';
import { EmployeeService } from 'src/app/core/services/employee.service';
import { FileService } from 'src/app/core/services/file.service';
import { OrganizationService } from 'src/app/core/services/organization.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { TariffService } from 'src/app/core/services/tariff.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { Convertor } from 'src/app/core/utils/convertor';
import { ObservableUtil } from 'src/app/core/utils/observable-util';
import { Util } from 'src/app/core/utils/util';
import { StorageUsage } from 'src/app/core/models/storage-usage.interface';
import { EditableContact } from '../editable-contact';
import {
  ContactActivityGroupView,
  ContactActivityItemView,
  ContactOverviewMetadataView,
  ContactOverviewSummaryCardView,
  ContactOverviewTab,
} from './contact-overview.models';

@Component({
    selector: 'app-contact-overview',
    templateUrl: './contact-overview.component.html',
    styleUrl: './contact-overview.component.scss',
    providers: [DetailedContactFormModel],
    standalone: false
})
export class ContactOverviewComponent extends EditableContact {
  contactForm = inject(DetailedContactFormModel);
  private readonly contactService = inject(ContactService);
  private readonly organizationService = inject(OrganizationService);
  private readonly fileService = inject(FileService);
  private readonly employeeService = inject(EmployeeService);
  private readonly permissionService = inject(PermissionService);
  private readonly tariffService = inject(TariffService);
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(NgbModal);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);

  @ViewChild('dangerTpl') public dangerTpl!: TemplateRef<unknown>;

  public readonly notFound = Constants.NotFound;
  public readonly imageExtensions = Constants.ImageExtensions;
  public readonly activityDateFormat = Constants.DateFormats.get('ua-date') ?? 'dd.MM.yyyy';
  public readonly dateTimeFormat = Constants.DateFormats.get('ua') ?? 'dd.MM.yyyy HH:mm';
  private readonly tabDefinitions: TabItem[] = [
    {
      id: 'overview',
      labelKey: 'COMPONENTS.CONTACT_OVERVIEW.TABS.OVERVIEW',
      icon: 'columns',
    },
    {
      id: 'activity',
      labelKey: 'COMPONENTS.CONTACT_OVERVIEW.TABS.ACTIVITY',
      icon: 'activity',
    },
    {
      id: 'notes',
      labelKey: 'COMPONENTS.CONTACT_OVERVIEW.TABS.NOTES',
      icon: 'file-text',
    },
    {
      id: 'files',
      labelKey: 'COMPONENTS.CONTACT_OVERVIEW.TABS.FILES',
      icon: 'paperclip',
    },
  ];

  public readonly canEditContact$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Contact,
          UserCompanyPermissions.Edit,
        ),
      ),
      shareReplay(1),
    );

  public readonly canDeleteContact$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Contact,
          UserCompanyPermissions.Delete,
        ),
      ),
      shareReplay(1),
    );

  public readonly canCreateOrganization$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Organization,
          UserCompanyPermissions.Create,
        ),
      ),
      shareReplay(1),
    );

  public readonly canUploadFiles$ = this.canEditContact$.pipe(
    switchMap((canEditContact) =>
      this.tariffService.companyQuotas$.pipe(
        map((companyQuotas) => {
          const quotaKey = Convertor.pascalToScreamingSnakeCase(Constants.QuotaKeys.s3Size);
          const storageQuota = companyQuotas.find((quota) => quota.key === quotaKey);

          if (!storageQuota) {
            return false;
          }

          return canEditContact && storageQuota.limit > storageQuota.used;
        }),
      ),
    ),
    shareReplay(1),
  );

  public readonly storageUsage$ = this.tariffService.companyQuotas$.pipe(
    map((companyQuotas): StorageUsage => {
      const quotaKey = Convertor.pascalToScreamingSnakeCase(Constants.QuotaKeys.s3Size);
      const storageQuota = companyQuotas.find((quota) => quota.key === quotaKey);
      const limit = storageQuota?.limit ?? 0;
      const used = storageQuota?.used ?? 0;

      return {
        used,
        available: Math.max(limit - used, 0),
        limit,
        usedPercent: limit > 0 ? Math.round((used / limit) * 1000) / 10 : 0,
      };
    }),
  );

  public prevImageExtension?: string;
  public isLoading = false;
  public isMobileViewport = false;
  public isCreateOrganizationModalOpen = false;
  public isActivityLoading = false;
  public isNotesLoading = false;
  public isFilesLoading = false;
  public isSubmittingNote = false;
  public callbackError?: string;
  public contactImagePath = '';
  public contactAttachmentsPath = '';
  public companyId = '';
  public contactsBackUrl = '';
  public organizationRoute: string[] = [];
  public readonly emptyFileListParam: Record<string, string> = {
    uk: 'файлах',
    en: 'File list',
  };
  public readonly storageLimitParam: Record<string, string> = {
    uk: 'сховища',
    en: 'storage',
  };

  public readonly contact$ = new BehaviorSubject<DetailedContact | null>(null);
  public avatarUrl$ = new Observable<string>();
  public readonly activeTab$ = new BehaviorSubject<ContactOverviewTab>('overview');
  public readonly phoneActionMenuItems: ActionMenuItem[] = [
    {
      id: 'call',
      labelKey: 'BUTTONS.CALL',
      icon: 'call',
    },
  ];
  public readonly editOnlyActionMenuItems: ActionMenuItem[] = [
    {
      id: 'edit',
      labelKey: 'BUTTONS.EDIT',
      icon: 'edit',
    },
  ];
  public readonly phoneActionMenuItemsWithEdit: ActionMenuItem[] = [
    ...this.phoneActionMenuItems,
    ...this.editOnlyActionMenuItems,
  ];
  public readonly emailActionMenuItems: ActionMenuItem[] = [
    {
      id: 'write',
      labelKey: 'BUTTONS.WRITE',
      icon: 'write',
    },
  ];
  public readonly emailActionMenuItemsWithEdit: ActionMenuItem[] = [
    ...this.emailActionMenuItems,
    ...this.editOnlyActionMenuItems,
  ];
  public readonly contactPosition$ = this.contact$.pipe(
    map((contact) => contact?.position ?? null),
  );
  public readonly contactIdentifier$ = this.contact$.pipe(
    map((contact) => {
      if (!contact) {
        return null;
      }

      return contact.businessCentralId ? contact.number || contact.businessCentralId : contact.id;
    }),
  );
  public readonly contactMetadata$ = this.contact$.pipe(
    map((contact): ContactOverviewMetadataView => {
      const createdAt = this.normalizeMetadataDate(contact?.createdAt);
      const updatedAt = this.normalizeMetadataDate(contact?.lastModifiedDateTime) ?? createdAt;

      return {
        createdAt,
        updatedAt,
      };
    }),
    shareReplay(1),
  );
  public readonly contactActivity$ = new BehaviorSubject<ContactActivity[]>([]);
  public readonly contactNotes$ = new BehaviorSubject<ContactNote[]>([]);
  public readonly contactFiles$ = new BehaviorSubject<AttachmentWithDetails[]>([]);
  public readonly newNoteControl = new FormControl('', { nonNullable: true });
  public readonly canSubmitNote$ = this.newNoteControl.valueChanges.pipe(
    startWith(this.newNoteControl.value),
    map((value) => value.trim().length > 0),
  );
  public readonly tabItems$ = combineLatest([
    this.contactNotes$,
    this.contactFiles$,
    this.contactActivity$,
    this.contact$,
  ]).pipe(
    map(([notes, files, activity]) =>
      this.tabDefinitions.map((tab) => {
        if (tab.id === 'activity') {
          return {
            ...tab,
            badgeCount: activity.length || null,
          };
        }

        if (tab.id === 'notes') {
          return {
            ...tab,
            badgeCount: notes.length || null,
          };
        }

        if (tab.id === 'files') {
          return {
            ...tab,
            badgeCount: files.length || null,
          };
        }

        return {
          ...tab,
          badgeCount: null,
        };
      }),
    ),
  );
  public readonly activityGroups$ = this.contactActivity$.pipe(
    map((items) => this.mapActivityGroups(items)),
  );
  public readonly summaryCards$ = combineLatest([
    this.contact$,
    this.contactActivity$,
    this.contactNotes$,
    this.contactFiles$,
  ]).pipe(
    map(([contact, activity, notes, files]): ContactOverviewSummaryCardView[] => {
      const lastActivityCreatedAt = activity.length
        ? activity.reduce((latest, item) =>
          new Date(item.createdAt).getTime() > new Date(latest.createdAt).getTime() ? item : latest)
        : null;
      const lastActivityValue = lastActivityCreatedAt
        ? this.formatDateTimeLabel(lastActivityCreatedAt.createdAt)
        : null;

      return [
        {
          id: 'delivery-addresses',
          icon: 'map-pin',
          toneClass: 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400',
          labelKey: 'COMPONENTS.CONTACT_OVERVIEW.SUMMARY.DELIVERY_ADDRESSES',
          value: String(contact?.deliveryAddresses.length ?? 0),
          valueClass: 'text-neutral-900 dark:text-neutral-50',
          valueSizeClass: 'text-2xl',
        },
        {
          id: 'activity',
          icon: 'activity',
          toneClass: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400',
          labelKey: 'COMPONENTS.CONTACT_OVERVIEW.SUMMARY.ACTIVITY_ITEMS',
          value: String(activity.length),
          valueClass: 'text-neutral-900 dark:text-neutral-50',
          valueSizeClass: 'text-2xl',
        },
        {
          id: 'notes',
          icon: 'file-text',
          toneClass: 'bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-400',
          labelKey: 'COMPONENTS.CONTACT_OVERVIEW.SUMMARY.NOTES',
          value: String(notes.length),
          valueClass: 'text-neutral-900 dark:text-neutral-50',
          valueSizeClass: 'text-2xl',
        },
        {
          id: 'files',
          icon: 'paperclip',
          toneClass: 'bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400',
          labelKey: 'COMPONENTS.CONTACT_OVERVIEW.SUMMARY.FILES',
          value: String(files.length),
          valueClass: 'text-neutral-900 dark:text-neutral-50',
          valueSizeClass: 'text-2xl',
        },
        {
          id: 'last-activity',
          icon: 'activity',
          toneClass: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
          labelKey: 'COMPONENTS.CONTACT_OVERVIEW.SUMMARY.LAST_ACTIVITY',
          value: lastActivityValue ?? '—',
          valueClass: 'text-neutral-900 dark:text-neutral-50',
          valueSizeClass: 'text-sm',
        },
      ];
    }),
  );
  public readonly companyOrganizations$ = new BehaviorSubject<MinimalOrganization[]>([]);
  public companyEmployees$!: Observable<MinimalProfile[]>;
  public responsibleUser$!: Observable<MinimalProfile | null>;

  private contactId = '';

  constructor() {
    const route = inject(ActivatedRoute);

    super();
    this.updateViewportState();

    const initUrl$ = route.params.pipe(
      first(),
      tap((params) => {
        this.contactId = params['id'];
        this.companyId = route.parent?.parent?.snapshot.params['id'];
        this.contactsBackUrl = `/companies/${this.companyId}/crm/contacts`;
        this.contactAttachmentsPath = `companies/${this.companyId}/contacts/${this.contactId}/attachments`;

        this.organizationService.setOrganizationsUrl(this.companyId);
        this.contactService.setContactsUrl(this.companyId);
        this.employeeService.setEmployeesUrl(this.companyId);
      }),
      shareReplay(1),
    );

    initUrl$
      .pipe(
        switchMap(() => this.contactService.getContact(this.contactId)),
        tap((contact) => {
          this.prevImageExtension = contact.avatarImageExtension;
          this.contactImagePath = `contacts/${contact.id}`;

          if (!this.prevImageExtension) {
            this.avatarUrl$ = of(Constants.NotFound).pipe(shareReplay(1));
          } else {
            this.avatarUrl$ = this.fileService
              .getFileUrl(`${this.contactImagePath}/avatar${contact.avatarImageExtension}`, true)
              .pipe(shareReplay(1));
          }

          this.applyContact(contact);
          this.loadTabData();
        }),
      )
      .subscribe();

    this.companyEmployees$ = initUrl$.pipe(
      switchMap(() => this.employeeService.getCompanyEmployeesLookup()),
      shareReplay(1),
    );

    this.responsibleUser$ = combineLatest([
      this.companyEmployees$,
      this.contact$,
    ]).pipe(
      map(([employees, contact]) => employees.find((employee) => employee.id === contact?.responsibleUserId) || null),
      filter((profile): profile is MinimalProfile => !!profile),
      switchMap((profile) => ObservableUtil.getProfileWithImage$(profile, this.fileService)),
    );

    initUrl$
      .pipe(
        switchMap(() => this.organizationService.getMinimalOrganizations()),
        tap((organizations) => this.companyOrganizations$.next(organizations)),
      )
      .subscribe();
  }

  public onUpdateInput(fieldName: string): void {
    const newValue = this.contactForm.form.get(fieldName)?.value;

    this.contactService
      .patchContact(this.contactId, fieldName, newValue)
      .pipe(
        first(),
        tap(() => {
          const currentContact = this.contact$.getValue();
          if (!currentContact) {
            return;
          }

          const formFieldNames = this.contactForm.fieldNames;
          const updatedContact: DetailedContact = {
            ...currentContact,
            [fieldName]: newValue,
          };

          if (
            [
              formFieldNames.name,
              formFieldNames.surname,
              formFieldNames.patronymic,
            ].includes(fieldName)
          ) {
            updatedContact.fullName = this.buildFullName(
              updatedContact.name,
              updatedContact.surname,
              updatedContact.patronymic,
            );
          }

          this.applyContact(updatedContact);
        }),
      )
      .subscribe();
  }

  public onUpdateOrganization(): void {
    this.isLoading = true;

    const newValue = this.contactForm.form.controls.organizationId.value;
    const fieldName = this.contactForm.fieldNames.organizationId;

    this.contactService
      .patchContact(this.contactId, fieldName, newValue)
      .pipe(
        first(),
        tap(() => {
          const currentContact = this.contact$.getValue();
          if (!currentContact) {
            return;
          }

          const updatedContact: DetailedContact = {
            ...currentContact,
            [fieldName]: newValue,
            organizationTitle:
              this.companyOrganizations$
                .getValue()
                .find((organization) => organization.id === newValue)?.title || '',
          };

          this.applyContact(updatedContact);
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe();
  }

  public onUploadFile(event: CustomFile): void {
    this.updateAvatarUrl(event.file.name);
  }

  public onDeleteFile(): void {
    this.updateAvatarUrl();
  }

  public onUploadContactFile(event: CustomFile): void {
    if (!event.upload) {
      return;
    }

    const attachment: Attachment = {
      id: Util.getGuidFromTitle(event.title) ?? globalThis.crypto?.randomUUID?.() ?? Util.generateGUID(),
      title: event.file.name,
      fileSize: event.file.size,
      isFolder: false,
      companyId: this.companyId,
      uploadId: event.upload.uploadId,
      s3Bucket: event.upload.bucket,
      s3Key: event.upload.key,
      eTag: event.upload.eTag,
      versionId: event.upload.versionId,
      contentType: event.upload.contentType,
      createdAtUtc: event.upload.createdAtUtc,
    };

    this.contactService
      .createContactFile(this.contactId, attachment, event.upload.correlationId)
      .pipe(
        first(),
        tap((createdAttachment) => {
          this.contactFiles$.next([
            this.mapContactFileView(createdAttachment),
            ...this.contactFiles$.getValue(),
          ]);
        }),
        catchError(() => this.rollbackUploadedContactFile(event.upload!.key)),
      )
      .subscribe();
  }

  public updateResponsibleUser(fieldName: string, popover: NgbPopover): void {
    this.onUpdateInput(fieldName);
    popover.close();
  }

  public selectTab(tabId: string): void {
    if (!this.isContactOverviewTab(tabId) || this.activeTab$.getValue() === tabId) {
      return;
    }

    this.activeTab$.next(tabId);
  }

  public createNote(): void {
    const text = this.newNoteControl.value.trim();
    if (!text || this.isSubmittingNote) {
      return;
    }

    this.isSubmittingNote = true;
    this.contactService
      .createContactNote(this.contactId, text)
      .pipe(
        first(),
        tap((note) => {
          this.contactNotes$.next([note, ...this.contactNotes$.getValue()]);
          this.contactActivity$.next([
            {
              id: note.id,
              type: 'note-added',
              description: note.text,
              createdAt: note.createdAt,
              createdBy: note.createdBy,
            },
            ...this.contactActivity$.getValue(),
          ]);
          this.newNoteControl.setValue('');
        }),
        catchError(() => {
          this.showErrorToast('ERRORS.UNKNOWN_ERROR');
          return EMPTY;
        }),
        finalize(() => {
          this.isSubmittingNote = false;
        }),
      )
      .subscribe();
  }

  public onContactDataActionSelect(actionId: string, startEdit: () => void, value?: string | null): void {
    if (actionId === 'edit') {
      startEdit();
      return;
    }

    if (!value || typeof window === 'undefined') {
      return;
    }

    if (actionId === 'call') {
      window.location.href = `tel:${value}`;
      return;
    }

    if (actionId === 'write') {
      window.location.href = `mailto:${value}`;
    }
  }

  public handleContactFileAction(file: AttachmentWithDetails, action?: 'open' | 'download'): void {
    const path = file.s3Key ?? `${this.contactAttachmentsPath}/${Util.getFileFullName(file)}`;
    const fileAction = action ?? file.fileActionName ?? 'download';
    const action$: Observable<unknown> =
      fileAction === 'open'
        ? this.fileService.getFileUrl(path, false).pipe(
            tap((url) => window.open(url, '_blank')),
          )
        : this.fileService.downloadFile(path, file.title).pipe(
            tap((blob) => Util.saveAs(blob, file.title)),
          );

    action$
      .pipe(
        first(),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  public deleteContactFile(file: AttachmentWithDetails): void {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      size: 'sm',
    });

    modalRef.componentInstance.message = Util.translateWithParam(
      this.translateService,
      'WARNINGS.DELETE',
      {
        uk: 'файл',
        en: 'file',
      },
    );

    modalRef.closed
      .pipe(
        first(),
        filter((result) => !!result),
        switchMap(() => {
          const path = file.s3Key ?? `${this.contactAttachmentsPath}/${Util.getFileFullName(file)}`;
          return this.fileService.delete(path).pipe(
            first(),
            switchMap(() => this.contactService.deleteContactFile(this.contactId, file.id)),
            tap(() => {
              this.contactFiles$.next(this.contactFiles$.getValue().filter((item) => item.id !== file.id));
            }),
            catchError(() => {
              this.showErrorToast('ERRORS.UNKNOWN_ERROR');
              this.loadContactFiles();
              return EMPTY;
            }),
          );
        }),
      )
      .subscribe();
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    this.updateViewportState();
  }

  public onDeleteUploadedContactFile(): void {}

  public onReachedStorageLimit(): void {
    this.callbackError = this.translateService.instant('TOOLTIPS.LIMIT_REACHED', {
      entity: this.storageLimitParam[this.translateService.currentLang || 'en'],
    });

    this.toastService.show({
      template: this.dangerTpl,
      className: 'bg-danger text-light',
    });
  }

  public deleteContactFileById(fileId: string): void {
    const file = this.contactFiles$.getValue().find((item) => item.id === fileId);
    if (!file) {
      return;
    }

    this.deleteContactFile(file);
  }

  public updateContactType(): void {
    const { isCustomer, isVendor } = this.contactForm.form.controls.contactType.value;

    this.contactService
      .patchContactFields(this.contactId, { isCustomer, isVendor })
      .pipe(
        first(),
        tap(() => {
          const currentContact = this.contact$.getValue();
          if (!currentContact) {
            return;
          }

          this.applyContact({
            ...currentContact,
            isCustomer,
            isVendor,
          });
        }),
      )
      .subscribe();
  }

  public openCreateOrganizationModal(): void {
    this.isCreateOrganizationModalOpen = true;
  }

  public closeCreateOrganizationModal(): void {
    this.isCreateOrganizationModalOpen = false;
  }

  public onOrganizationCreated(organization: MinimalOrganization): void {
    this.closeCreateOrganizationModal();
    this.companyOrganizations$.next([
      ...this.companyOrganizations$.getValue(),
      organization,
    ]);

    this.contactForm.form.controls.organizationId.setValue(organization.id);
    this.onUpdateOrganization();
  }

  public deleteContact(): void {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      size: 'sm',
    });

    modalRef.componentInstance.message = Util.translateWithParam(
      this.translateService,
      `WARNINGS.DELETE`,
      {
        uk: 'контакт',
        en: 'contact',
      },
    );

    modalRef.closed
      .pipe(
        first(),
        filter((res) => !!res),
        switchMap(() => this.contactService.deleteContact(this.contactId)),
        switchMap(() =>
          this.avatarUrl$.pipe(
            first(),
            switchMap((url) => this.fileService.delete(url)),
          ),
        ),
        tap(() => {
          this.router.navigateByUrl(`companies/${this.companyId}/crm/contacts`);
        }),
      )
      .subscribe();
  }

  public onDeliveryAddressesChanged(deliveryAddresses: ContactDeliveryAddress[]): void {
    const currentContact = this.contact$.getValue();
    if (!currentContact) {
      return;
    }

    this.contact$.next({
      ...currentContact,
      deliveryAddresses,
    });
  }

  private loadTabData(): void {
    this.loadContactActivity();
    this.loadContactNotes();
    this.loadContactFiles();
  }

  private loadContactActivity(): void {
    this.isActivityLoading = true;
    this.contactService
      .getContactActivity(this.contactId)
      .pipe(
        first(),
        tap((activity) => this.contactActivity$.next(activity)),
        catchError(() => {
          this.contactActivity$.next([]);
          return EMPTY;
        }),
        finalize(() => {
          this.isActivityLoading = false;
        }),
      )
      .subscribe();
  }

  private loadContactNotes(): void {
    this.isNotesLoading = true;
    this.contactService
      .getContactNotes(this.contactId)
      .pipe(
        first(),
        tap((notes) => this.contactNotes$.next(notes)),
        catchError(() => {
          this.contactNotes$.next([]);
          return EMPTY;
        }),
        finalize(() => {
          this.isNotesLoading = false;
        }),
      )
      .subscribe();
  }

  private loadContactFiles(): void {
    this.isFilesLoading = true;
    this.contactService
      .getContactFiles(this.contactId)
      .pipe(
        first(),
        map((files) => files.map((file) => this.mapContactFileView(file))),
        tap((files) => this.contactFiles$.next(files)),
        catchError(() => {
          this.contactFiles$.next([]);
          return EMPTY;
        }),
        finalize(() => {
          this.isFilesLoading = false;
        }),
      )
      .subscribe();
  }

  private applyContact(contact: DetailedContact): void {
    this.contact$.next(contact);
    this.contactForm.form.patchValue(contact);
    this.contactForm.form.controls.contactType.setValue({
      isCustomer: !!contact.isCustomer,
      isVendor: !!contact.isVendor,
    });
    this.organizationRoute = [
      '/companies',
      this.companyId,
      'crm',
      'organizations',
      contact.organizationId ?? '',
    ];
  }

  private rollbackUploadedContactFile(s3Key: string): Observable<never> {
    return this.fileService.delete(s3Key).pipe(
      first(),
      catchError(() => of(null)),
      tap(() => {
        this.showErrorToast('ERRORS.FILE_UPLOAD_METADATA_SAVE_FAILED');
      }),
      switchMap(() => EMPTY),
    );
  }

  private updateAvatarUrl(fileName?: string): void {
    const extensionName = Util.getExtensionFromFileTitle(fileName);
    const newImageExtension = extensionName ? `.${extensionName}` : undefined;

    this.avatarUrl$ = this.contactService
      .patchContact(this.contactId, 'avatarImageExtension', newImageExtension)
      .pipe(
        first(),
        switchMap(() =>
          this.fileService.getFileUrl(
            `${this.contactImagePath}/avatar${newImageExtension}`,
            true,
          ),
        ),
      )
      .pipe(shareReplay(1));
  }

  private updateViewportState(): void {
    this.isMobileViewport = typeof window !== 'undefined' && window.innerWidth < 768;
  }

  private normalizeMetadataDate(value?: string | null): string | null {
    if (!value || value.startsWith('0001-01-01')) {
      return null;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime()) || date.getUTCFullYear() <= 1) {
      return null;
    }

    return value;
  }

  private mapContactFileView(file: Attachment): AttachmentWithDetails {
    return {
      ...file,
      imageSrc: Util.getAttachmentImage(file.title),
      extensionName: Util.getExtensionFromFileTitle(file.mappedTitle || file.title),
      fileActionName: Util.canBeOpenedFile(file) ? 'open' : 'download',
      uploadedByName: file.createdBy ?? null,
      uploadedAtLabel: this.formatDateTimeLabel(file.createdAt ?? file.createdAtUtc ?? null),
      canRemove: true,
      canRename: false,
    };
  }

  private mapActivityGroups(items: ContactActivity[]): ContactActivityGroupView[] {
    const groups = new Map<string, ContactActivityItemView[]>();

    items.forEach((item) => {
      const date = new Date(item.createdAt);
      const dayLabel = this.formatActivityDayLabel(date);
      const groupItems = groups.get(dayLabel) ?? [];
      groupItems.push({
        id: item.id,
        title: this.mapActivityTitle(item.type),
        description: item.description,
        createdBy: item.createdBy,
        timeLabel: new Intl.DateTimeFormat(this.translateService.currentLang || 'en', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).format(date),
        icon: this.mapActivityIcon(item.type),
        dotClass: this.mapActivityDotClass(item.type),
        hasConnector: false,
      });
      groups.set(dayLabel, groupItems);
    });

    return Array.from(groups.entries()).map(([dayLabel, groupItems]) => ({
      dayLabel,
      items: groupItems.map((groupItem, index) => ({
        ...groupItem,
        hasConnector: index < groupItems.length - 1,
      })),
    }));
  }

  private mapActivityTitle(type: ContactActivity['type']): string {
    switch (type) {
      case 'note-added':
        return this.translateService.instant('COMPONENTS.CONTACT_OVERVIEW.ACTIVITY.NOTE_ADDED');
      case 'file-uploaded':
        return this.translateService.instant('COMPONENTS.CONTACT_OVERVIEW.ACTIVITY.FILE_UPLOADED');
      default:
        return this.translateService.instant('COMPONENTS.CONTACT_OVERVIEW.ACTIVITY.CONTACT_CREATED');
    }
  }

  private mapActivityIcon(type: ContactActivity['type']): ContactActivityItemView['icon'] {
    switch (type) {
      case 'note-added':
        return 'note';
      case 'file-uploaded':
        return 'file';
      default:
        return 'activity';
    }
  }

  private mapActivityDotClass(type: ContactActivity['type']): string {
    switch (type) {
      case 'note-added':
        return 'contact-overview-page__timeline-dot--note';
      case 'file-uploaded':
        return 'contact-overview-page__timeline-dot--file';
      default:
        return 'contact-overview-page__timeline-dot--activity';
    }
  }

  private formatActivityDayLabel(date: Date): string {
    return formatDate(date, this.activityDateFormat, this.translateService.currentLang || 'en');
  }

  private formatDateTimeLabel(value?: string | Date | null): string | null {
    if (!value) {
      return null;
    }

    return new Intl.DateTimeFormat(this.translateService.currentLang || 'en', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  private buildFullName(name: string, surname?: string | null, patronymic?: string | null): string {
    return [surname, name, patronymic].filter((part) => !!part && part.trim().length > 0).join(' ');
  }

  private isContactOverviewTab(tabId: string): tabId is ContactOverviewTab {
    return this.tabDefinitions.some((tab) => tab.id === tabId);
  }

  private showErrorToast(messageKey: string): void {
    this.callbackError = this.translateService.instant(messageKey);
    this.toastService.show({
      template: this.dangerTpl,
      className: 'bg-danger text-light',
    });
  }
}
